package com.orderpanel.strings

import kotlinx.browser.window
import org.w3c.xhr.XMLHttpRequest

private val cache = mutableMapOf<String, AboutStrings>()

private val xmlStringRegex = Regex("""<string name="([^"]+)">([\s\S]*?)</string>""")

private fun parseXml(xml: String): Map<String, String> =
    xmlStringRegex.findAll(xml).associate { it.groupValues[1] to it.groupValues[2].unescapeXml() }

private fun String.unescapeXml() = this
    .replace("&amp;", "&")
    .replace("&lt;", "<")
    .replace("&gt;", ">")
    .replace("&quot;", "\"")
    .replace("&apos;", "'")

private fun Map<String, String>.toAboutStrings(): AboutStrings {
    fun s(key: String) = getOrElse(key, {""})
    return AboutStrings(
        // ── Shared / About page ──────────────────────────────────────────────
        brand = s("brand"),
        navHome = s("navHome"),
        navAboutUs = s("navAboutUs"),
        navLogin = s("navLogin"),
        navGetStarted = s("navGetStarted"),
        heroLabel = s("heroLabel"),
        heroHeadlinePre = s("heroHeadlinePre"),
        heroHeadlineHighlight = s("heroHeadlineHighlight"),
        heroBody = s("heroBody"),
        stat1Value = s("stat1Value"),
        stat1Label = s("stat1Label"),
        stat2Value = s("stat2Value"),
        stat2Label = s("stat2Label"),
        stat3Value = s("stat3Value"),
        stat3Label = s("stat3Label"),
        stat4Value = s("stat4Value"),
        stat4Label = s("stat4Label"),
        missionLabel = s("missionLabel"),
        missionTitle = s("missionTitle"),
        missionBody1Pre = s("missionBody1Pre"),
        missionBody1Highlight = s("missionBody1Highlight"),
        missionBody1Post = s("missionBody1Post"),
        missionBody2 = s("missionBody2"),
        feature1 = s("feature1"),
        feature2 = s("feature2"),
        feature3 = s("feature3"),
        feature4 = s("feature4"),
        feature5 = s("feature5"),
        valuesLabel = s("valuesLabel"),
        valuesTitle = s("valuesTitle"),
        value1Title = s("value1Title"),
        value1Desc = s("value1Desc"),
        value2Title = s("value2Title"),
        value2Desc = s("value2Desc"),
        value3Title = s("value3Title"),
        value3Desc = s("value3Desc"),
        value4Title = s("value4Title"),
        value4Desc = s("value4Desc"),
        value5Title = s("value5Title"),
        value5Desc = s("value5Desc"),
        value6Title = s("value6Title"),
        value6Desc = s("value6Desc"),
        ctaTitle = s("ctaTitle"),
        ctaBody = s("ctaBody"),
        ctaButton = s("ctaButton"),
        footerCopyright = s("footerCopyright"),
        footerHome = s("footerHome"),
        footerLogin = s("footerLogin"),
        footerSignup = s("footerSignup"),

        // ── Index (landing) page ─────────────────────────────────────────────
        idxPageTitle = s("idxPageTitle"),
        idxNavFeatures = s("idxNavFeatures"),
        idxNavPricing = s("idxNavPricing"),
        idxNavFaq = s("idxNavFaq"),
        idxHeroBadgeNew = s("idxHeroBadgeNew"),
        idxHeroBadgeText = s("idxHeroBadgeText"),
        idxHeroHeadline1 = s("idxHeroHeadline1"),
        idxHeroHeadline2 = s("idxHeroHeadline2"),
        idxHeroSubtext = s("idxHeroSubtext"),
        idxHeroPrimaryBtn = s("idxHeroPrimaryBtn"),
        idxHeroSecondaryBtn = s("idxHeroSecondaryBtn"),
        idxHeroTrustLine = s("idxHeroTrustLine"),
        idxSocialProof1 = s("idxSocialProof1"),
        idxSocialProof2 = s("idxSocialProof2"),
        idxSocialProof3 = s("idxSocialProof3"),
        idxSocialProof4 = s("idxSocialProof4"),
        idxFeaturesLabel = s("idxFeaturesLabel"),
        idxFeaturesTitle = s("idxFeaturesTitle"),
        idxFeaturesSubtitle = s("idxFeaturesSubtitle"),
        idxFeat1Title = s("idxFeat1Title"),
        idxFeat1Desc = s("idxFeat1Desc"),
        idxFeat2Title = s("idxFeat2Title"),
        idxFeat2Desc = s("idxFeat2Desc"),
        idxFeat3Title = s("idxFeat3Title"),
        idxFeat3Desc = s("idxFeat3Desc"),
        idxFeat4Title = s("idxFeat4Title"),
        idxFeat4Desc = s("idxFeat4Desc"),
        idxFeat5Title = s("idxFeat5Title"),
        idxFeat5Desc = s("idxFeat5Desc"),
        idxFeat6Title = s("idxFeat6Title"),
        idxFeat6Desc = s("idxFeat6Desc"),
        idxPricingLabel = s("idxPricingLabel"),
        idxPricingTitle = s("idxPricingTitle"),
        idxPricingSubtitle = s("idxPricingSubtitle"),
        idxPricingAnnualNote = s("idxPricingAnnualNote"),
        idxPricingAnnualLink = s("idxPricingAnnualLink"),
        idxPlan1Name = s("idxPlan1Name"),
        idxPlan1Price = s("idxPlan1Price"),
        idxPlan1Period = s("idxPlan1Period"),
        idxPlan1Desc = s("idxPlan1Desc"),
        idxPlan1Feat1 = s("idxPlan1Feat1"),
        idxPlan1Feat2 = s("idxPlan1Feat2"),
        idxPlan1Feat3 = s("idxPlan1Feat3"),
        idxPlan1Feat4 = s("idxPlan1Feat4"),
        idxPlan1Feat5 = s("idxPlan1Feat5"),
        idxPlan1Cta = s("idxPlan1Cta"),
        idxPlan2Name = s("idxPlan2Name"),
        idxPlan2Price = s("idxPlan2Price"),
        idxPlan2Period = s("idxPlan2Period"),
        idxPlan2Desc = s("idxPlan2Desc"),
        idxPlan2Feat1 = s("idxPlan2Feat1"),
        idxPlan2Feat2 = s("idxPlan2Feat2"),
        idxPlan2Feat3 = s("idxPlan2Feat3"),
        idxPlan2Feat4 = s("idxPlan2Feat4"),
        idxPlan2Feat5 = s("idxPlan2Feat5"),
        idxPlan2Feat6 = s("idxPlan2Feat6"),
        idxPlan2Feat7 = s("idxPlan2Feat7"),
        idxPlan2Feat8 = s("idxPlan2Feat8"),
        idxPlan2Cta = s("idxPlan2Cta"),
        idxPlan2Badge = s("idxPlan2Badge"),
        idxPlan3Name = s("idxPlan3Name"),
        idxPlan3Price = s("idxPlan3Price"),
        idxPlan3Period = s("idxPlan3Period"),
        idxPlan3Desc = s("idxPlan3Desc"),
        idxPlan3Feat1 = s("idxPlan3Feat1"),
        idxPlan3Feat2 = s("idxPlan3Feat2"),
        idxPlan3Feat3 = s("idxPlan3Feat3"),
        idxPlan3Feat4 = s("idxPlan3Feat4"),
        idxPlan3Feat5 = s("idxPlan3Feat5"),
        idxPlan3Feat6 = s("idxPlan3Feat6"),
        idxPlan3Feat7 = s("idxPlan3Feat7"),
        idxPlan3Cta = s("idxPlan3Cta"),
        idxFaqLabel = s("idxFaqLabel"),
        idxFaqTitle = s("idxFaqTitle"),
        idxFaqSubtitle = s("idxFaqSubtitle"),
        idxFaq1Q = s("idxFaq1Q"),
        idxFaq1A = s("idxFaq1A"),
        idxFaq2Q = s("idxFaq2Q"),
        idxFaq2A = s("idxFaq2A"),
        idxFaq3Q = s("idxFaq3Q"),
        idxFaq3A = s("idxFaq3A"),
        idxFaq4Q = s("idxFaq4Q"),
        idxFaq4A = s("idxFaq4A"),
        idxFaq5Q = s("idxFaq5Q"),
        idxFaq5A = s("idxFaq5A"),
        idxFaq6Q = s("idxFaq6Q"),
        idxFaq6A = s("idxFaq6A"),
        idxCtaTitle = s("idxCtaTitle"),
        idxCtaBody = s("idxCtaBody"),
        idxCtaBtn = s("idxCtaBtn"),
        idxFooterTagline = s("idxFooterTagline"),
        idxFooterGroupProduct = s("idxFooterGroupProduct"),
        idxFooterProd1 = s("idxFooterProd1"),
        idxFooterProd2 = s("idxFooterProd2"),
        idxFooterProd3 = s("idxFooterProd3"),
        idxFooterProd4 = s("idxFooterProd4"),
        idxFooterGroupCompany = s("idxFooterGroupCompany"),
        idxFooterComp1 = s("idxFooterComp1"),
        idxFooterComp2 = s("idxFooterComp2"),
        idxFooterComp3 = s("idxFooterComp3"),
        idxFooterComp4 = s("idxFooterComp4"),
        idxFooterGroupLegal = s("idxFooterGroupLegal"),
        idxFooterLegal1 = s("idxFooterLegal1"),
        idxFooterLegal2 = s("idxFooterLegal2"),
        idxFooterLegal3 = s("idxFooterLegal3"),
        idxFooterCopyright = s("idxFooterCopyright"),
        idxFooterTwitter = s("idxFooterTwitter"),
        idxFooterGithub = s("idxFooterGithub"),
        idxFooterLinkedin = s("idxFooterLinkedin"),

        // ── Login page ───────────────────────────────────────────────────────
        logHeading = s("logHeading"),
        logSubtext = s("logSubtext"),
        logGoogleBtn = s("logGoogleBtn"),
        logDivider = s("logDivider"),
        logEmailLabel = s("logEmailLabel"),
        logEmailPlaceholder = s("logEmailPlaceholder"),
        logPasswordLabel = s("logPasswordLabel"),
        logPasswordPlaceholder = s("logPasswordPlaceholder"),
        logForgot = s("logForgot"),
        logSubmit = s("logSubmit"),
        logSubmitLoading = s("logSubmitLoading"),
        logNoAccount = s("logNoAccount"),
        logSignupLink = s("logSignupLink"),
        logValidationEmailPassword = s("logValidationEmailPassword"),

        // ── Signup page ──────────────────────────────────────────────────────
        sigFreePlanBadge = s("sigFreePlanBadge"),
        sigHeading = s("sigHeading"),
        sigSubtext = s("sigSubtext"),
        sigGoogleBtn = s("sigGoogleBtn"),
        sigDivider = s("sigDivider"),
        sigNameLabel = s("sigNameLabel"),
        sigNamePlaceholder = s("sigNamePlaceholder"),
        sigEmailLabel = s("sigEmailLabel"),
        sigEmailPlaceholder = s("sigEmailPlaceholder"),
        sigPasswordLabel = s("sigPasswordLabel"),
        sigPasswordPlaceholder = s("sigPasswordPlaceholder"),
        sigConfirmLabel = s("sigConfirmLabel"),
        sigConfirmPlaceholder = s("sigConfirmPlaceholder"),
        sigSubmit = s("sigSubmit"),
        sigSubmitLoading = s("sigSubmitLoading"),
        sigTermsPre = s("sigTermsPre"),
        sigTermsLink = s("sigTermsLink"),
        sigTermsAnd = s("sigTermsAnd"),
        sigPrivacyLink = s("sigPrivacyLink"),
        sigTermsPost = s("sigTermsPost"),
        sigHasAccount = s("sigHasAccount"),
        sigLoginLink = s("sigLoginLink"),
        sigValidationFillAll = s("sigValidationFillAll"),
        sigValidationPasswordMatch = s("sigValidationPasswordMatch"),
        sigValidationPasswordLength = s("sigValidationPasswordLength"),

        // ── Admin page ───────────────────────────────────────────────────────
        admPageTitle = s("admPageTitle"),
        admTopbarOrders = s("admTopbarOrders"),
        admTopbarAllMonths = s("admTopbarAllMonths"),
        admTopbarAllYears = s("admTopbarAllYears"),
        admTopbarSignOut = s("admTopbarSignOut"),
        admNavLabel = s("admNavLabel"),
        admNavOrders = s("admNavOrders"),
        admNavShipment = s("admNavShipment"),
        admNavHistory = s("admNavHistory"),
        admNavAppInfo = s("admNavAppInfo"),
        admNavLoss = s("admNavLoss"),
        admLossTitle = s("admLossTitle"),
        admLossSubtitle = s("admLossSubtitle"),
        admLossAddSection = s("admLossAddSection"),
        admLossReason = s("admLossReason"),
        admLossCustomerName = s("admLossCustomerName"),
        admLossSku = s("admLossSku"),
        admLossCost = s("admLossCost"),
        admLossDate = s("admLossDate"),
        admLossRecordBtn = s("admLossRecordBtn"),
        admLossRecording = s("admLossRecording"),
        admLossTableSection = s("admLossTableSection"),
        admLossColDate = s("admLossColDate"),
        admLossColReason = s("admLossColReason"),
        admLossColName = s("admLossColName"),
        admLossColSku = s("admLossColSku"),
        admLossColCost = s("admLossColCost"),
        admLossDeleteBtn = s("admLossDeleteBtn"),
        admLossEmpty = s("admLossEmpty"),
        admLossReasonRefund = s("admLossReasonRefund"),
        admLossReasonReplacement = s("admLossReasonReplacement"),
        admLossReasonDamage = s("admLossReasonDamage"),
        admLossReasonOther = s("admLossReasonOther"),
        admDeleteTitle = s("admDeleteTitle"),
        admDeleteBody = s("admDeleteBody"),
        admDeleteCancel = s("admDeleteCancel"),
        admDeleteConfirm = s("admDeleteConfirm"),
        admClearAllTitle = s("admClearAllTitle"),
        admClearAllBodyPre = s("admClearAllBodyPre"),
        admClearAllBodyPost = s("admClearAllBodyPost"),
        admClearAllCancel = s("admClearAllCancel"),
        admClearAllConfirm = s("admClearAllConfirm"),
        admClearAllDeleting = s("admClearAllDeleting"),
        admCsvOverlayTitle = s("admCsvOverlayTitle"),
        admCsvOverlayBody = s("admCsvOverlayBody"),
        admCsvInfoTitle = s("admCsvInfoTitle"),
        admCsvInfoBody = s("admCsvInfoBody"),
        admCsvInfoRequired = s("admCsvInfoRequired"),
        admCsvInfoOptional = s("admCsvInfoOptional"),
        admCsvInfoContinue = s("admCsvInfoContinue"),
        admImportBtn = s("admImportBtn"),
        admImportingBtn = s("admImportingBtn"),
        admNewOrderBtn = s("admNewOrderBtn"),
        admCancelFormBtn = s("admCancelFormBtn"),
        admFormHeader = s("admFormHeader"),
        admFormSectionOrder = s("admFormSectionOrder"),
        admFormSectionFinancials = s("admFormSectionFinancials"),
        admFormFieldOrderDate = s("admFormFieldOrderDate"),
        admFormFieldQty = s("admFormFieldQty"),
        admFormFieldStatus = s("admFormFieldStatus"),
        admFormFieldReceiverName = s("admFormFieldReceiverName"),
        admFormFieldTrackingNumber = s("admFormFieldTrackingNumber"),
        admFormFieldCargoCompany = s("admFormFieldCargoCompany"),
        admFormFieldShippingDate = s("admFormFieldShippingDate"),
        admFormFieldSku = s("admFormFieldSku"),
        admFormFieldCountry = s("admFormFieldCountry"),
        admFormFieldCustomValue = s("admFormFieldCustomValue"),
        admFormFieldProfit = s("admFormFieldProfit"),
        admFormFieldShipCost = s("admFormFieldShipCost"),
        admFormFieldProdCost = s("admFormFieldProdCost"),
        admFormFieldTariffUS = s("admFormFieldTariffUS"),
        admFormFieldTariffNonUS = s("admFormFieldTariffNonUS"),
        admFormNetProfitLabel = s("admFormNetProfitLabel"),
        admFormSaveBtn = s("admFormSaveBtn"),
        admFormSavingBtn = s("admFormSavingBtn"),
        admFormErrorRequired = s("admFormErrorRequired"),
        admFormErrorDate = s("admFormErrorDate"),
        admFormSelectSkuPlaceholder = s("admFormSelectSkuPlaceholder"),
        admStatusAll = s("admStatusAll"),
        admStatusProcessing = s("admStatusProcessing"),
        admStatusShipped = s("admStatusShipped"),
        admStatusDelivered = s("admStatusDelivered"),
        admSortBy = s("admSortBy"),
        admSortDateDesc = s("admSortDateDesc"),
        admSortDateAsc = s("admSortDateAsc"),
        admSortRevenue = s("admSortRevenue"),
        admSearchPlaceholder = s("admSearchPlaceholder"),
        admDateFrom = s("admDateFrom"),
        admDateTo = s("admDateTo"),
        admStatTotalOrders = s("admStatTotalOrders"),
        admStatDelivered = s("admStatDelivered"),
        admStatInTransit = s("admStatInTransit"),
        admStatProcessing = s("admStatProcessing"),
        admStatGrossProfit = s("admStatGrossProfit"),
        admStatNetProfit = s("admStatNetProfit"),
        admStatShippingAds = s("admStatShippingAds"),
        admStatTariffProdCost = s("admStatTariffProdCost"),
        admTableDate = s("admTableDate"),
        admTableQty = s("admTableQty"),
        admTableReceiver = s("admTableReceiver"),
        admTableSku = s("admTableSku"),
        admTableTracking = s("admTableTracking"),
        admTableCargo = s("admTableCargo"),
        admTableShipDate = s("admTableShipDate"),
        admTableCountry = s("admTableCountry"),
        admTableStatus = s("admTableStatus"),
        admTableProfit = s("admTableProfit"),
        admTableShipCost = s("admTableShipCost"),
        admTableTariff = s("admTableTariff"),
        admTableProdCost = s("admTableProdCost"),
        admTableNetProfit = s("admTableNetProfit"),
        admAllOrders = s("admAllOrders"),
        admNoOrdersTitle = s("admNoOrdersTitle"),
        admNoOrdersBodyPrefix = s("admNoOrdersBodyPrefix"),
        admLoadingOrders = s("admLoadingOrders"),
        admNoMatchFilters = s("admNoMatchFilters"),
        admClearAll = s("admClearAll"),
        admRecordsOf = s("admRecordsOf"),
        admRecords = s("admRecords"),
        admEditTooltip = s("admEditTooltip"),
        admCloneTooltip = s("admCloneTooltip"),
        admDeleteTooltip = s("admDeleteTooltip"),
        admSaveEdit = s("admSaveEdit"),
        admCancelEdit = s("admCancelEdit"),
        admOrdersUnit = s("admOrdersUnit"),
        admItemsUnit = s("admItemsUnit"),
        admAppInfoTitle = s("admAppInfoTitle"),
        admShopProfileSection = s("admShopProfileSection"),
        admLogoSaved = s("admLogoSaved"),
        admChangeLogo = s("admChangeLogo"),
        admNoLogo = s("admNoLogo"),
        admUploading = s("admUploading"),
        admShopNamePlaceholder = s("admShopNamePlaceholder"),
        admSaveNameBtn = s("admSaveNameBtn"),
        admSavingNameBtn = s("admSavingNameBtn"),
        admSkuSection = s("admSkuSection"),
        admSkuPlaceholder = s("admSkuPlaceholder"),
        admSkuCostPlaceholder = s("admSkuCostPlaceholder"),
        admEstDelivery = s("admEstDelivery"),
        admAddSkuBtn = s("admAddSkuBtn"),
        admAddSkuSavingBtn = s("admAddSkuSavingBtn"),
        admSkuErrorEmpty = s("admSkuErrorEmpty"),
        admSkuErrorInvalidCost = s("admSkuErrorInvalidCost"),
        admSkuErrorNegative = s("admSkuErrorNegative"),
        admNoSkus = s("admNoSkus"),
        admDayUnit = s("admDayUnit"),
        admDaysUnit = s("admDaysUnit"),
        admDeliveryLabel = s("admDeliveryLabel"),
        admShipmentsTitle = s("admShipmentsTitle"),
        admShipmentsSubtext = s("admShipmentsSubtext"),
        admShipOverdue = s("admShipOverdue"),
        admShipEarly = s("admShipEarly"),
        admShipOnTime = s("admShipOnTime"),
        admShipPending = s("admShipPending"),
        admShipNoConfig = s("admShipNoConfig"),
        admShipSectionOverdue = s("admShipSectionOverdue"),
        admShipSectionEarly = s("admShipSectionEarly"),
        admShipSectionOnTime = s("admShipSectionOnTime"),
        admShipSectionPending = s("admShipSectionPending"),
        admShipSectionNoConfig = s("admShipSectionNoConfig"),
        admShipNoConfigNote = s("admShipNoConfigNote"),
        admShipNoOrders = s("admShipNoOrders"),
        admShipNoDeliveryConfig = s("admShipNoDeliveryConfig"),
        admShipTableName = s("admShipTableName"),
        admShipTableSku = s("admShipTableSku"),
        admShipTableOrderDate = s("admShipTableOrderDate"),
        admShipTableShipped = s("admShipTableShipped"),
        admShipTableEstDeadline = s("admShipTableEstDeadline"),
        admShipTableStatus = s("admShipTableStatus"),
        admShipTableDaysEarly = s("admShipTableDaysEarly"),
        admShipTableDaysLeft = s("admShipTableDaysLeft"),
        admShipLate = s("admShipLate"),
        admShipNotShipped = s("admShipNotShipped"),
        admShipEarlyAdj = s("admShipEarlyAdj"),
        admShipLeftAdj = s("admShipLeftAdj"),
        admDistributionTitle = s("admDistributionTitle"),
        admDistributionPct = s("admDistributionPct"),
        admDistMonthly = s("admDistMonthly"),
        admDistYearly = s("admDistYearly"),
        admDistCostBreakdown = s("admDistCostBreakdown"),
        admDistCountry = s("admDistCountry"),
        admDistSku = s("admDistSku"),
        admDistTotal = s("admDistTotal"),
        admDistNoSkuData = s("admDistNoSkuData"),
        admAnalyticsTitle = s("admAnalyticsTitle"),
        admAnalyticsInsights = s("admAnalyticsInsights"),
        admAnalyticsTopRevSku = s("admAnalyticsTopRevSku"),
        admAnalyticsBestNetSku = s("admAnalyticsBestNetSku"),
        admAnalyticsMostOrdered = s("admAnalyticsMostOrdered"),
        admAnalyticsBestCountry = s("admAnalyticsBestCountry"),
        admAnalyticsAvgNet = s("admAnalyticsAvgNet"),
        admAnalyticsDelivRate = s("admAnalyticsDelivRate"),
        admAnalyticsHighestGross = s("admAnalyticsHighestGross"),
        admAnalyticsAfterAllCosts = s("admAnalyticsAfterAllCosts"),
        admAnalyticsOfMonthVol = s("admAnalyticsOfMonthVol"),
        admAnalyticsNetProfitMonth = s("admAnalyticsNetProfitMonth"),
        admAnalyticsOrdersThisMonth = s("admAnalyticsOrdersThisMonth"),
        admAnalyticsAvgNetDesc = s("admAnalyticsAvgNetDesc"),
        admAnalyticsFulfillRate = s("admAnalyticsFulfillRate"),
        admAnalyticsOrders = s("admAnalyticsOrders"),
        admYearlyBreakdownTitle = s("admYearlyBreakdownTitle"),
        admYearlyBreakdownSub = s("admYearlyBreakdownSub"),
        admYearlyRevenue = s("admYearlyRevenue"),
        admYearlyNetProfit = s("admYearlyNetProfit"),
        admYearlyShipping = s("admYearlyShipping"),
        admYearlyAds = s("admYearlyAds"),
        admYearlyProdCost = s("admYearlyProdCost"),
        admYearlyTariff = s("admYearlyTariff"),
        admMonthlyTitle = s("admMonthlyTitle"),
        admMonthlyLast12 = s("admMonthlyLast12"),
        admHistTitle = s("admHistTitle"),
        admHistSubtext = s("admHistSubtext"),
        admHistSelectMonth = s("admHistSelectMonth"),
        admHistRecordExists = s("admHistRecordExists"),
        admHistMonthlySummary = s("admHistMonthlySummary"),
        admHistOrdersByCountry = s("admHistOrdersByCountry"),
        admHistOrdersBySku = s("admHistOrdersBySku"),
        admHistNetIncomeAuto = s("admHistNetIncomeAuto"),
        admHistOrderCount = s("admHistOrderCount"),
        admHistTotalShipCost = s("admHistTotalShipCost"),
        admHistTotalTariff = s("admHistTotalTariff"),
        admHistTotalAdExpense = s("admHistTotalAdExpense"),
        admHistTotalProdCost = s("admHistTotalProdCost"),
        admHistCrossFit = s("admHistCrossFit"),
        admHistSave = s("admHistSave"),
        admHistUpdate = s("admHistUpdate"),
        admHistSaving = s("admHistSaving"),
        admHistDelete = s("admHistDelete"),
        admHistDeleting = s("admHistDeleting"),
        admHistSavedSuffix = s("admHistSavedSuffix"),
        admHistSavedMonths = s("admHistSavedMonths"),
        admHistTableMonth = s("admHistTableMonth"),
        admHistTableOrders = s("admHistTableOrders"),
        admHistTableShipping = s("admHistTableShipping"),
        admHistTableTariff = s("admHistTableTariff"),
        admHistTableAds = s("admHistTableAds"),
        admHistTableNetIncome = s("admHistTableNetIncome"),
        admHistTableProdCost = s("admHistTableProdCost"),
        admHistTableCrossFit = s("admHistTableCrossFit"),
        admHistDeleteConfirmPre = s("admHistDeleteConfirmPre"),
        admHistDeleteConfirmPost = s("admHistDeleteConfirmPost"),
        admHistDeleted = s("admHistDeleted"),
        admErrDeleteOrder = s("admErrDeleteOrder"),
        admErrClearOrders = s("admErrClearOrders"),
        admErrSaveFailed = s("admErrSaveFailed"),
        admErrUpdateOrder = s("admErrUpdateOrder"),
        admErrLogoUpload = s("admErrLogoUpload"),
        admErrGeneric = s("admErrGeneric"),
        admSavedMsg = s("admSavedMsg"),
        admLogoSavedMsg = s("admLogoSavedMsg"),
        admCsvErrEmpty = s("admCsvErrEmpty"),
        admCsvErrMissingCols = s("admCsvErrMissingCols"),
        admCsvErrNoValid = s("admCsvErrNoValid"),
        admCsvSkipped = s("admCsvSkipped"),
        admCsvImported = s("admCsvImported"),
        admFormPlaceholderFullName = s("admFormPlaceholderFullName"),
        admFormPlaceholderTracking = s("admFormPlaceholderTracking"),
        admFormPlaceholderCustomValue = s("admFormPlaceholderCustomValue"),
        admEditPlaceholderTracking = s("admEditPlaceholderTracking"),
        admLogoUploadBtn = s("admLogoUploadBtn"),
        admShopNameNotSet = s("admShopNameNotSet"),
        admSkusCountLabel = s("admSkusCountLabel"),
        admLossTotal = s("admLossTotal"),
        admFsInFirestore = s("admFsInFirestore"),
        admFsFailedDecode = s("admFsFailedDecode"),

        // ── Month names ──────────────────────────────────────────────────────
        monthJanuary = s("monthJanuary"),
        monthFebruary = s("monthFebruary"),
        monthMarch = s("monthMarch"),
        monthApril = s("monthApril"),
        monthMay = s("monthMay"),
        monthJune = s("monthJune"),
        monthJuly = s("monthJuly"),
        monthAugust = s("monthAugust"),
        monthSeptember = s("monthSeptember"),
        monthOctober = s("monthOctober"),
        monthNovember = s("monthNovember"),
        monthDecember = s("monthDecember"),
        monthShortJan = s("monthShortJan"),
        monthShortFeb = s("monthShortFeb"),
        monthShortMar = s("monthShortMar"),
        monthShortApr = s("monthShortApr"),
        monthShortMay = s("monthShortMay"),
        monthShortJun = s("monthShortJun"),
        monthShortJul = s("monthShortJul"),
        monthShortAug = s("monthShortAug"),
        monthShortSep = s("monthShortSep"),
        monthShortOct = s("monthShortOct"),
        monthShortNov = s("monthShortNov"),
        monthShortDec = s("monthShortDec"),

        // ── Onboarding ───────────────────────────────────────────────────────
        admOnbTitle = s("admOnbTitle"),
        admOnbSubtitle = s("admOnbSubtitle"),
        admOnbSkip = s("admOnbSkip"),
        admOnbNext = s("admOnbNext"),
        admOnbBack = s("admOnbBack"),
        admOnbFinish = s("admOnbFinish"),
        admOnbStep1Title = s("admOnbStep1Title"), admOnbStep1Desc = s("admOnbStep1Desc"),
        admOnbStep2Title = s("admOnbStep2Title"), admOnbStep2Desc = s("admOnbStep2Desc"),
        admOnbStep3Title = s("admOnbStep3Title"), admOnbStep3Desc = s("admOnbStep3Desc"),
        admOnbStep4Title = s("admOnbStep4Title"), admOnbStep4Desc = s("admOnbStep4Desc"),
        admOnbStep5Title = s("admOnbStep5Title"), admOnbStep5Desc = s("admOnbStep5Desc"),
    )
}

private fun loadXml(fileName: String): String? = try {
    val xhr = XMLHttpRequest()
    xhr.open("GET", "/resources/$fileName", false)
    xhr.send()
    if (xhr.status == 200.toShort()) xhr.responseText else null
} catch (_: Throwable) { null }

fun getStrings(): AboutStrings {
    val lang = window.navigator.language
    val fileName = if (lang.startsWith("tr", ignoreCase = true)) "strings_tr.xml" else "strings_en.xml"
    return cache.getOrPut(fileName) {
        val xml = loadXml(fileName)
        parseXml(xml ?: "").toAboutStrings()
    }
}
